import React, { useEffect, useMemo, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, Clock, Award, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Overview } from "@/components/evidence/Overview";
import { ResidentTimeline } from "@/components/evidence/ResidentTimeline";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => fmtDate(iso);
const TONE = { green: "text-rag-green bg-rag-green-bg", amber: "text-amber-700 bg-amber-50", red: "text-rag-red bg-rag-red-bg" };
const PRACTICE = { met: { label: "Met", cls: "text-rag-green bg-rag-green-bg", Icon: CheckCircle2 }, attention: { label: "Needs attention", cls: "text-rag-amber bg-rag-amber-bg", Icon: AlertTriangle }, not_met: { label: "Not met", cls: "text-rag-red bg-rag-red-bg", Icon: XCircle } };

function PracticeSummary({ areas, practice }) {
  return (
    <div data-testid="inspector-practice" className="mt-8 rounded-3xl bg-white border border-cream-deep p-6 md:p-8">
      <h4 className="font-heading font-bold text-brand text-sm">Step 2 — Practice confirmed by the manager</h4>
      <p className="text-xs text-muted-foreground mb-4">Platform records are never marked compliant automatically; the manager confirms day-to-day practice in four areas.</p>
      <ul className="divide-y divide-cream">
        {areas.map((a) => {
          const saved = practice[a.key] || {};
          const st = PRACTICE[saved.status];
          return (
            <li key={a.key} data-testid={`inspector-area-${a.key}`} className="py-3 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-brand">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.prompts.join(" · ")}</div>
                {saved.action && <div className="text-xs mt-1"><span className="font-semibold">Action:</span> {saved.action}</div>}
              </div>
              {st ? <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`}><st.Icon className="h-3.5 w-3.5" />{st.label}</span>
                  : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-cream text-muted-foreground">Not yet confirmed</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ActionPlanSummary({ findings }) {
  const open = findings.filter((f) => !f.completed_at);
  return (
    <div data-testid="inspector-actions" className="mt-8 rounded-3xl bg-white border border-cream-deep p-6 md:p-8">
      <h4 className="font-heading font-bold text-brand text-sm">Step 3 — Action plan</h4>
      <p className="text-xs text-muted-foreground mb-4">{open.length} open action{open.length === 1 ? "" : "s"} · {findings.length - open.length} completed</p>
      {findings.length === 0 ? <p className="text-sm text-muted-foreground">No open findings.</p> : (
        <ul className="divide-y divide-cream text-sm">
          {findings.map((f) => (
            <li key={f.key} className={`py-2 flex flex-wrap justify-between gap-2 ${f.completed_at ? "opacity-60" : ""}`}>
              <div><span className="text-xs text-muted-foreground">{f.scope} · {f.domain}</span><div className="font-medium text-brand">{f.item}</div>{f.action && <div className="text-xs">{f.action}</div>}</div>
              <div className="text-xs text-muted-foreground text-right">{f.owner && <div>Owner: {f.owner}</div>}{f.deadline && <div>Due {fmt(f.deadline)}</div>}{f.completed_at && <div className="text-rag-green font-semibold">Done {fmt(f.completed_at)}</div>}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function InspectorView() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/inspector/${token}`).then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This inspector link has expired or been revoked."));
  }, [token]);

  const a = info?.assurance || {};
  const address = useMemo(() => [info?.care_home?.town, info?.care_home?.postcode].filter(Boolean).join(", "), [info]);
  return (
    <div className="min-h-screen bg-cream font-body" data-testid="inspector-page">
      <PublicNav />
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-16 pb-20 text-left">
        <span className="label-caps text-coral">Inspector access</span>
        {error ? (
          <div data-testid="inspector-error" className="mt-6 rounded-3xl bg-white border border-rag-red/30 p-8">
            <h1 className="text-2xl font-bold text-brand">Link unavailable</h1>
            <p className="text-muted-foreground mt-2">{error} Please ask the care home manager for a new link.</p>
          </div>
        ) : !info ? <p className="mt-6 text-muted-foreground">Loading…</p> : (
          <>
            <div className="flex items-center gap-4 mt-4">
              {info.care_home?.logo_url && <img src={info.care_home.logo_url} alt="" className="h-14 max-w-[140px] object-contain" />}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-brand tracking-tight">{info.care_home?.name}</h1>
                <p className="text-muted-foreground">{address}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2"><Clock className="h-4 w-4" />Shared by {info.shared_by || "the care home manager"}{info.note ? ` · ${info.note}` : ""} · link expires {fmt(info.expires_at)}</p>

            <div className="mt-8 rounded-3xl bg-white border border-cream-deep p-6 md:p-8" data-testid="inspector-summary">
              <div className="flex items-center gap-3 text-brand"><ShieldCheck className="h-7 w-7" /><span className="font-heading font-bold text-xl">Oral Health Assurance Review</span></div>
              <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
                <div className="rounded-xl bg-cream p-4"><div className="text-muted-foreground">Last assessment</div><div className="font-semibold text-brand mt-1">{fmt(a.last_assessment_at)}</div></div>
                <div className="rounded-xl bg-cream p-4"><div className="text-muted-foreground">Signed-off score</div><div className="font-semibold text-brand mt-1">{a.last_score != null ? `${a.last_score}%` : "—"}{a.rag && <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${TONE[a.rag] || ""}`}>{a.status_label}</span>}</div></div>
                <div className="rounded-xl bg-cream p-4"><div className="text-muted-foreground flex items-center gap-1"><Award className="h-3.5 w-3.5" />Certificates held</div><div className="font-semibold text-brand mt-1">{info.certificates}</div></div>
              </div>
              <Button asChild className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white h-11 px-6">
                <a href={`${API}/api/inspector/${token}/evidence-pack.pdf`} target="_blank" rel="noreferrer" data-testid="inspector-pdf-link"><FileText className="h-4 w-4 mr-2" />Open the Evidence Pack (PDF)</a>
              </Button>
              <p className="text-xs text-muted-foreground mt-3">Generated live from the home's current records — overview, practice confirmation, resident sample audit, action plan and staff training certificates.</p>
            </div>

            {info.overview && (
              <div className="mt-8 rounded-3xl bg-white border border-cream-deep p-6 md:p-8">
                <Overview overview={info.overview} readOnly />
                {info.score && (
                  <p className="text-xs text-muted-foreground -mt-4" data-testid="inspector-score-line">
                    Live score (changes as records are updated; the signed-off score above is from the last approved review): <b>{info.score.overall_pct}%</b> ({info.score.overall?.label}) · practice {info.score.practice?.pct ?? "—"}% · resident audit {info.score.resident_pct ?? "—"}% · {info.score.sample?.audited}/{info.score.sample?.size} residents audited
                  </p>
                )}
              </div>
            )}
            {info.practice_areas && <PracticeSummary areas={info.practice_areas} practice={info.practice || {}} />}
            {info.findings && <ActionPlanSummary findings={info.findings} />}
            {info.residents && (
              <div className="mt-8 rounded-3xl bg-white border border-cream-deep p-6 md:p-8 [&>section]:mt-0">
                <ResidentTimeline residents={info.residents} readOnly />
              </div>
            )}
          </>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
